import styles from "../../pages/Community/CreateCommunityPage.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import VideoTabsManager from "../VideoTabsManager";

const ImageUploadField = ({ label, imageUrl, onUpload, onDelete, isSubmitting }) => (
  <div className={styles.imageSectionWrapper}>
    <label className={styles.inlineLabel}>{label}</label>
    <div className={styles.featuredImageBox}>
      {imageUrl ? (
        <>
          <img src={imageUrl} alt={label} className={styles.featuredImageBox} />
          <button
            type="button"
            onClick={onDelete}
            className={styles.removeImageButton}
            disabled={isSubmitting}
            title="Remove image"
          >
            ✕
          </button>
        </>
      ) : (
        <div className={styles.iconPlaceholderContainer}>
          <img src={placeholderImage} alt="Placeholder" className={styles.iconPlaceholder} />
          <div className={styles.uploadLinks}>
            <label className={styles.uploadLink}>
              <span className={styles.plusIcon}>+</span> Choose {label}
              <input type="file" accept="image/*" hidden onChange={onUpload} disabled={isSubmitting} />
            </label>
          </div>
        </div>
      )}
    </div>
  </div>
);

// Per-section title / description / image editor, bound to the active section.
const SectionContentForm = ({ section, update, isSubmitting }) => {
  const uploadImage = (fileKey, urlKey, deleteKey) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    update(fileKey, file);
    update(urlKey, URL.createObjectURL(file));
    update(deleteKey, false);
  };
  const deleteImage = (fileKey, urlKey, deleteKey) => () => {
    update(fileKey, null);
    update(urlKey, "");
    update(deleteKey, true);
  };

  return (
    <div className={styles.sectionsCard}>
      <div className={styles.rowInputGroup}>
        <label className={styles.inlineLabel}>Section Title</label>
        <input
          type="text"
          value={section.section_title}
          onChange={(e) => update("section_title", e.target.value)}
          className={styles.textInput}
          disabled={isSubmitting}
        />
      </div>

      <ImageUploadField
        label="Section Banner (Website)"
        imageUrl={section.banner_web_url}
        onUpload={uploadImage("banner_web_file", "banner_web_url", "delete_banner_web")}
        onDelete={deleteImage("banner_web_file", "banner_web_url", "delete_banner_web")}
        isSubmitting={isSubmitting}
      />
      <ImageUploadField
        label="Section Banner (Mobile App)"
        imageUrl={section.banner_app_url}
        onUpload={uploadImage("banner_app_file", "banner_app_url", "delete_banner_app")}
        onDelete={deleteImage("banner_app_file", "banner_app_url", "delete_banner_app")}
        isSubmitting={isSubmitting}
      />

      <div className={styles.rowInputGroup}>
        <label className={styles.inlineLabel}>Primary Text</label>
        <textarea
          value={section.section_primary_text}
          onChange={(e) => update("section_primary_text", e.target.value)}
          className={styles.textAreaLarge}
          rows="4"
          placeholder="Use **text** for bold formatting"
          disabled={isSubmitting}
        />
      </div>
      <span className={styles.hint}>Tip: Use **text** to make text bold</span>

      <ImageUploadField
        label="Section Image (Website)"
        imageUrl={section.image_web_url}
        onUpload={uploadImage("image_web_file", "image_web_url", "delete_image_web")}
        onDelete={deleteImage("image_web_file", "image_web_url", "delete_image_web")}
        isSubmitting={isSubmitting}
      />
      <ImageUploadField
        label="Section Image (Mobile App)"
        imageUrl={section.image_app_url}
        onUpload={uploadImage("image_app_file", "image_app_url", "delete_image_app")}
        onDelete={deleteImage("image_app_file", "image_app_url", "delete_image_app")}
        isSubmitting={isSubmitting}
      />

      <div className={styles.rowInputGroup}>
        <label className={styles.inlineLabel}>Secondary Text</label>
        <textarea
          value={section.section_secondary_text}
          onChange={(e) => update("section_secondary_text", e.target.value)}
          className={styles.textAreaLarge}
          rows="4"
          placeholder="Use **text** for bold formatting"
          disabled={isSubmitting}
        />
      </div>
      <span className={styles.hint}>Tip: Use **text** to make text bold</span>
    </div>
  );
};

const PageSectionsManager = ({ pageSections, isSubmitting }) => {
  const {
    sections,
    activeSectionIndex,
    setActiveSectionIndex,
    activeSection,
    tabsState,
    updateActiveSectionField,
    addSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
  } = pageSections;

  return (
    <div className={styles.sectionsWrapper}>
      <label className={styles.inlineLabel}>Page Sections</label>

      <div className={styles.sectionsCard}>
        <button
          className={styles.submitButton}
          onClick={addSection}
          disabled={isSubmitting}
        >
          <span>Add Section</span>
          <span className={styles.submitIcon}>+</span>
        </button>

        <div className={styles.sectionList}>
          {sections.map((section, index) => (
            <div
              key={index}
              className={`${styles.sectionRow} ${
                activeSectionIndex === index ? styles.activeRow : ""
              }`}
              onClick={() => setActiveSectionIndex(index)}
            >
              <div className={styles.reorderButtons}>
                <button
                  className={styles.arrowButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveSectionUp(index);
                  }}
                  disabled={index === 0 || isSubmitting}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  className={styles.arrowButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveSectionDown(index);
                  }}
                  disabled={index === sections.length - 1 || isSubmitting}
                  title="Move down"
                >
                  ▼
                </button>
              </div>

              <span className={styles.sectionLabel}>
                {section.section_title?.trim() || `Section ${index + 1}`}
              </span>

              <div className={styles.sectionActions}>
                <button
                  className={styles.deleteIconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSection(index);
                  }}
                  disabled={isSubmitting}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {activeSection && (
        <>
          <SectionContentForm
            section={activeSection}
            update={updateActiveSectionField}
            isSubmitting={isSubmitting}
          />
          <VideoTabsManager videoTabsState={tabsState} isSubmitting={isSubmitting} />
        </>
      )}
    </div>
  );
};

export default PageSectionsManager;
